import React, { useEffect } from 'react';
import { TextElement } from '../../../core/template.schemas';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { useGoogleFonts, FontFamily, FontVariant } from '../../hooks/useGoogleFonts';
import { Label } from '@/ui-components/ui/label';
import { Switch } from '@/ui-components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/ui-components/ui/toggle-group';
import { Input } from '@/ui-components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui-components/ui/select';
import { Button } from '@/ui-components/ui/button';
import { 
  Type, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { CommonProperties } from './common';
import { AdvancedStaticDynamicSelector, elementAttributeRegistry } from '../../registry';

interface TextPropertiesProps {
  element: TextElement;
  onOpenSlotPanel?: () => void;
}

class FontOptionsService {
  private fonts: FontFamily[];
  private loadFont: (fontFamily: string, variants?: string[]) => void;
  private loadedFonts: Set<string> = new Set();

  constructor(fonts: FontFamily[], loadFont: (fontFamily: string, variants?: string[]) => void) {
    this.fonts = fonts;
    this.loadFont = loadFont;
  }

  /**
   * Get all available font families
   */
  getFontFamilies(): FontFamily[] {
    return this.fonts;
  }

  /**
   * Get variants for a specific font family
   */
  getVariantsForFamily(fontFamily: string): FontVariant[] {
    const font = this.fonts.find(f => f.family === fontFamily);
    return font?.variants || [];
  }

  /**
   * Load a font dynamically with caching
   */
  loadFontFamily(fontFamily: string, variants?: string[]): void {
    const cacheKey = `${fontFamily}-${variants?.join(',') || 'default'}`;
    
    if (this.loadedFonts.has(cacheKey)) {
      return; // Already loaded
    }
    
    this.loadFont(fontFamily, variants);
    this.loadedFonts.add(cacheKey);
  }

  /**
   * Preload all fonts for preview
   */
  preloadAllFonts(): void {
    this.fonts.forEach(font => {
      // Load regular variant for preview
      const regularVariant = font.variants.find(v => v.googleVariant === 'regular');
      if (regularVariant) {
        this.loadFontFamily(font.family, ['regular']);
      } else if (font.variants.length > 0) {
        // Fallback to first available variant
        this.loadFontFamily(font.family, [font.variants[0].googleVariant]);
      }
    });
  }

  /**
   * Check if a font is loaded
   */
  isFontLoaded(fontFamily: string): boolean {
    return this.loadedFonts.has(`${fontFamily}-400`) || this.loadedFonts.has(`${fontFamily}-default`);
  }

  /**
   * Get display name for a font weight
   */
  getWeightDisplayName(weight: string): string {
    const weightNames: Record<string, string> = {
      '100': 'Thin',
      '200': 'Extra Light',
      '300': 'Light',
      '400': 'Regular',
      '500': 'Medium',
      '600': 'Semi Bold',
      '700': 'Bold',
      '800': 'Extra Bold',
      '900': 'Black'
    };
    return weightNames[weight] || weight;
  }
}

/**
 * Controller class for font selection logic
 * Handles font family and weight selection state management
 */
class FontSelectionController {
  private element: TextElement;
  private fontOptionsService: FontOptionsService;
  private onPropertyChange: (property: keyof TextElement, value: any) => void;

  constructor(
    element: TextElement,
    fontOptionsService: FontOptionsService,
    onPropertyChange: (property: keyof TextElement, value: any) => void
  ) {
    this.element = element;
    this.fontOptionsService = fontOptionsService;
    this.onPropertyChange = onPropertyChange;
  }

  /**
   * Normalize font variant to separate weight and style
   */
  private parseVariant(variant: string): { weight: string; style: 'normal' | 'italic' } {
    if (variant === 'regular') {
      return { weight: '400', style: 'normal' };
    } else if (variant === 'italic') {
      return { weight: '400', style: 'italic' };
    } else if (variant.endsWith('italic')) {
      const weight = variant.replace('italic', '') || '400';
      return { weight, style: 'italic' };
    } else {
      return { weight: variant, style: 'normal' };
    }
  }

  /**
   * Get current font variant (combines weight and style)
   */
  getCurrentVariant(): string {
    const weight = this.element.fontWeight || '400';
    const style = this.element.fontStyle || 'normal';
    
    if (weight === '400' && style === 'normal') {
      return 'regular';
    } else if (weight === '400' && style === 'italic') {
      return 'italic';
    } else if (style === 'italic') {
      return `${weight}italic`;
    } else {
      return weight;
    }
  }

  /**
   * Handle font family selection
   */
  handleFontFamilyChange = (fontFamily: string): void => {
    // Load the font dynamically
    this.fontOptionsService.loadFontFamily(fontFamily);
    
    // Update the element
    this.onPropertyChange('fontFamily', fontFamily);
    
    // Reset font variant to regular if current variant is not available in new font
    const variants = this.fontOptionsService.getVariantsForFamily(fontFamily);
    const currentVariant = this.getCurrentVariant();
    const hasCurrentVariant = variants.some(v => v.googleVariant === currentVariant);
        
    if (!hasCurrentVariant && variants.length > 0) {
      // Find regular variant or fallback to first available
      const regularVariant = variants.find(v => v.googleVariant === 'regular') || variants[0];
            
      if (regularVariant) {
        this.handleVariantChange(regularVariant.googleVariant);
      }
    }
  };

  /**
   * Handle font variant selection (combines weight and style)
   */
  handleVariantChange = (variant: string): void => {    
    const parsed = this.parseVariant(variant);
    
    // Update both weight and style
    this.onPropertyChange('fontWeight', parsed.weight);
    this.onPropertyChange('fontStyle', parsed.style);
    
    // Store the original variant for compatibility
    this.onPropertyChange('fontVariant', variant);
  };

  /**
   * Get current font family with fallback
   */
  getCurrentFontFamily(): string {
    return this.element.fontFamily || 'Roboto';
  }

  /**
   * Get current font weight with fallback
   */
  getCurrentFontWeight(): string {
    return this.element.fontWeight || '400';
  }

  /**
   * Get current font style with fallback
   */
  getCurrentFontStyle(): 'normal' | 'italic' {
    return this.element.fontStyle || 'normal';
  }

  /**
   * Get available variants for current font family
   */
  getAvailableVariants(): FontVariant[] {
    return this.fontOptionsService.getVariantsForFamily(this.getCurrentFontFamily());
  }
}

/**
 * Text Properties Component
 * Displays text-specific properties like content, font, alignment, etc.
 */
export const TextProperties: React.FC<TextPropertiesProps> = ({ element, onOpenSlotPanel }) => {
  const updateElement = useTemplateStore(s => s.updateElement);
  const { fonts, loading: fontsLoading, error: fontsError, loadFont } = useGoogleFonts();
  
  // Get attribute mappings for text elements
  const textMapping = elementAttributeRegistry.get('text');
  const contentAttribute = textMapping?.attributes.find(attr => attr.attributeKey === 'content');
  const colorAttribute = textMapping?.attributes.find(attr => attr.attributeKey === 'color');

  // Initialize font services and controllers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fontOptionsService = new FontOptionsService(fonts, loadFont);
  const fontSelectionController = new FontSelectionController(
    element,
    fontOptionsService,
    (property, value) => updateElement(element.id, { [property]: value })
  );

  // Load current font when component mounts or fonts change
  useEffect(() => {
    if (!fontsLoading && fonts.length > 0) {
      // Load all variants for the element's font family for preview
      if (element.fontFamily) {
        const variants = fontOptionsService.getVariantsForFamily(element.fontFamily);
        const allVariantKeys = variants.map(v => v.googleVariant);
        fontOptionsService.loadFontFamily(element.fontFamily, allVariantKeys);
      }
      
      // Preload popular fonts for better UX (first 5 fonts from the list)
      const popularFonts = fonts.slice(0, 5);
      popularFonts.forEach(font => {
        fontOptionsService.loadFontFamily(font.family, ['regular']);
      });
    }
  }, [fontsLoading, fonts, element.fontFamily, element.fontWeight, element.fontStyle, fontOptionsService]);

  const handlePropertyChange = (property: keyof TextElement, value: any) => {
    updateElement(element.id, { [property]: value });
  };

  const handleContentChange = (newContent: any) => {
    handlePropertyChange('content', newContent);
  };

  const handleColorChange = (newColor: any) => {
    handlePropertyChange('color', newColor);
  };

  // Helper function to get current color value
  const getCurrentColor = () => {
    return element.color || colorAttribute?.defaultValue;
  };

  const handleAlignmentChange = (horizontal: 'left' | 'center' | 'right', vertical?: 'top' | 'center' | 'bottom') => {
    const newAlign = { ...element.align };
    if (horizontal) newAlign.h = horizontal;
    if (vertical) newAlign.v = vertical;
    handlePropertyChange('align', newAlign);
  };

  // Get current alignment values with defaults
  const currentHAlign = element.align?.h || 'left';
  const currentVAlign = element.align?.v || 'top';

  return (
    <div className="space-y-4">
      <CommonProperties element={element} />
      
      {/* Text-specific Properties */}
      <div className="space-y-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Type className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Text</Label>
        </div>
        
        {/* Text Content - Using AdvancedStaticDynamicSelector */}
        {contentAttribute && (
          <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
            <AdvancedStaticDynamicSelector
              label="Inhalt"
              value={element.content}
              onChange={handleContentChange}
              compatibleMediaTypes={contentAttribute.compatibleMediaTypes}
              required={contentAttribute.required}
              onOpenSlotPanel={onOpenSlotPanel}
            />
          </div>
        )}
        
        {/* Text Color */}
        {colorAttribute && (
          <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
            <AdvancedStaticDynamicSelector
              label="Textfarbe"
              value={getCurrentColor()}
              onChange={handleColorChange}
              compatibleMediaTypes={colorAttribute.compatibleMediaTypes}
              required={colorAttribute.required}
              onOpenSlotPanel={onOpenSlotPanel}
            />
          </div>
        )}
        
        {/* Font Selection Group */}
        <div className="grid grid-cols-2 gap-3">
          {/* Font Family */}
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500 font-medium">Schriftart</Label>
            {fontsLoading ? (
              <div className="h-8 bg-gray-100 rounded-lg animate-pulse" />
            ) : fontsError ? (
              <div className="text-[10px] text-red-500">Fehler beim Laden</div>
            ) : (
              <Select 
                value={fontSelectionController.getCurrentFontFamily()} 
                onValueChange={fontSelectionController.handleFontFamilyChange}
              >
                <SelectTrigger className="h-8 text-[11px] rounded-lg border-gray-200 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-gray-100 shadow-xl max-h-[300px]">
                  {fontOptionsService.getFontFamilies().map((font) => (
                    <SelectItem 
                      key={font.family} 
                      value={font.family}
                      className="text-[11px]"
                    >
                      <span style={{ fontFamily: `"${font.family}", sans-serif` }}>
                        {font.family}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Font Variant */}
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500 font-medium">Variante</Label>
            {fontsLoading ? (
              <div className="h-8 bg-gray-100 rounded-lg animate-pulse" />
            ) : (
              <Select 
                value={fontSelectionController.getCurrentVariant()} 
                onValueChange={fontSelectionController.handleVariantChange}
              >
                <SelectTrigger className="h-8 text-[11px] rounded-lg border-gray-200 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-gray-100 shadow-xl">
                  {fontSelectionController.getAvailableVariants().map((variant) => (
                    <SelectItem 
                      key={variant.googleVariant} 
                      value={variant.googleVariant}
                      className="text-[11px]"
                    >
                      {variant.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        
        {/* Font Sizing */}
        <div className="space-y-3 bg-gray-50/30 p-2 rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <Label className="text-[11px] text-gray-500 font-medium">Schriftgröße</Label>
            <ToggleGroup
              type="single"
              value={element.fontSizing?.mode ?? 'manual'}
              onValueChange={(value) => {
                if (value) {
                  handlePropertyChange('fontSizing', { 
                    ...element.fontSizing, 
                    mode: value as 'manual' | 'fit',
                    fontSize: value === 'manual' ? ((element.fontSizing as any)?.fontSize ?? 32) : undefined
                  });
                }
              }}
              className="bg-gray-100/80 p-0.5 rounded-lg border border-gray-200"
            >
              <ToggleGroupItem value="manual" className="text-[10px] h-6 px-2 rounded-md data-[state=on]:bg-white data-[state=on]:text-blue-600 data-[state=on]:shadow-sm" size="sm">
                Manuell
              </ToggleGroupItem>
              <ToggleGroupItem value="fit" className="text-[10px] h-6 px-2 rounded-md data-[state=on]:bg-white data-[state=on]:text-blue-600 data-[state=on]:shadow-sm" size="sm">
                Auto-Fit
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="grid grid-cols-[1fr,auto] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-[10px] text-gray-400 font-medium uppercase tracking-tight">Größe (px)</Label>
              <Input
                type="number"
                value={element.fontSizing?.mode === 'manual' ? (element.fontSizing as any).fontSize : 32}
                onChange={(e) => handlePropertyChange('fontSizing', {
                  ...element.fontSizing,
                  mode: 'manual',
                  fontSize: parseFloat(e.target.value) || 32
                })}
                className="h-8 text-[11px] rounded-lg border-gray-200 bg-white"
                min="1"
                disabled={element.fontSizing?.mode === 'fit'}
              />
            </div>
            <div 
              className={cn(
                "flex items-center gap-2 h-8 px-3 rounded-lg border border-gray-200 bg-white transition-opacity",
                element.fontSizing?.mode === 'fit' && "opacity-50"
              )}
            >
              <Label className="text-[11px] text-gray-500 font-medium cursor-pointer">Ellipse</Label>
              <Switch
                checked={element.fontSizing?.mode === 'manual' ? (element.fontSizing as any).ellipsis === '...' : false}
                onCheckedChange={(checked) => handlePropertyChange('fontSizing', {
                  ...element.fontSizing,
                  mode: 'manual',
                  ellipsis: checked ? '...' : undefined
                })}
                disabled={element.fontSizing?.mode === 'fit'}
                className="scale-75 origin-right data-[state=checked]:bg-blue-600"
              />
            </div>
          </div>
        </div>
        
        {/* Line Height */}
        <div className="space-y-1.5">
          <Label className="text-[11px] text-gray-500 font-medium">Zeilenhöhe</Label>
          <div className="relative">
            <Input
              type="number"
              value={element.lineHeight || 1.2}
              onChange={(e) => handlePropertyChange('lineHeight', parseFloat(e.target.value) || 1.2)}
              className="h-8 text-[11px] rounded-lg border-gray-200 pr-7"
              min="0.5"
              max="3"
              step="0.1"
            />
            <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">lh</span>
          </div>
        </div>
        
        {/* Text Alignment */}
        <div className="space-y-2">
          <Label className="text-[11px] text-gray-500 font-medium">Ausrichtung</Label>
          <div className="flex items-center justify-between p-1.5 bg-gray-50/50 rounded-xl border border-gray-100">
            {/* Horizontal Alignment */}
            <div className="flex bg-gray-100/50 p-0.5 rounded-lg gap-0.5">
              {[
                { id: 'left', icon: <AlignLeft className="w-3.5 h-3.5" /> },
                { id: 'center', icon: <AlignCenter className="w-3.5 h-3.5" /> },
                { id: 'right', icon: <AlignRight className="w-3.5 h-3.5" /> }
              ].map((align) => (
                <Button
                  key={align.id}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 w-8 p-0 rounded-md transition-all",
                    currentHAlign === align.id ? "bg-white text-blue-600 shadow-sm" : "text-gray-400 hover:text-gray-600"
                  )}
                  onClick={() => handleAlignmentChange(align.id as any, currentVAlign)}
                >
                  {align.icon}
                </Button>
              ))}
            </div>
            
            <div className="w-px h-4 bg-gray-200 mx-1" />

            {/* Vertical Alignment */}
            <div className="flex bg-gray-100/50 p-0.5 rounded-lg gap-0.5">
              {[
                { id: 'top', icon: <AlignVerticalJustifyStart className="w-3.5 h-3.5" /> },
                { id: 'center', icon: <AlignVerticalJustifyCenter className="w-3.5 h-3.5" /> },
                { id: 'bottom', icon: <AlignVerticalJustifyEnd className="w-3.5 h-3.5" /> }
              ].map((align) => (
                <Button
                  key={align.id}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 w-8 p-0 rounded-md transition-all",
                    currentVAlign === align.id ? "bg-white text-blue-600 shadow-sm" : "text-gray-400 hover:text-gray-600"
                  )}
                  onClick={() => handleAlignmentChange(currentHAlign, align.id as any)}
                >
                  {align.icon}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
